import { FileText, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatPence, SERVICES, useCheckBalance, useServicePrices } from '@/features/payments';
import {
  BalanceDisplay,
  InsufficientBalance,
} from '@/features/payments/components/insufficient-balance';

import { PRICE_LOADING_PLACEHOLDER } from '../lib/tier-pricing';

interface MatchReportModalProps {
  open: boolean;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  onPurchase: () => void;
  onSkip: () => void;
  onAddFunds: () => void;
  isPurchasing: boolean;
}

/**
 * Risk level badge component.
 */
function RiskLevelBadge({ level }: { level: 'LOW' | 'MEDIUM' | 'HIGH' }) {
  const colors = {
    LOW: 'bg-green-100 text-green-800',
    MEDIUM: 'bg-yellow-100 text-yellow-800',
    HIGH: 'bg-red-100 text-red-800',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${colors[level]}`}
    >
      {level} RISK
    </span>
  );
}

/**
 * Match Report Modal - shown after screening to offer detailed findings report.
 * Displays detected risk level and offers £7.50 report purchase.
 * Available to all users regardless of risk level.
 */
export function MatchReportModal({
  open,
  riskLevel,
  onPurchase,
  onSkip,
  onAddFunds,
  isPurchasing,
}: MatchReportModalProps) {
  const { data: balanceCheck, isLoading: isCheckingBalance } = useCheckBalance(
    SERVICES.MATCH_RISK_REPORT,
    open,
  );
  const { data: servicePrices } = useServicePrices();
  const eddPrice = servicePrices?.find((p) => p.key === SERVICES.AI_CONFIDENCE_REPORT);

  const hasSufficientBalance = balanceCheck?.sufficient ?? false;
  const currentBalance = balanceCheck?.currentBalancePence ?? 0;
  const priceLabel = balanceCheck
    ? formatPence(balanceCheck.requiredAmountPence)
    : PRICE_LOADING_PLACEHOLDER;

  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent className="sm:max-w-[600px]" hideCloseButton>
        <DialogHeader className="pt-4 text-center sm:text-center">
          <DialogTitle className="text-[20px] font-semibold text-[#051923]">
            Purchase Match Report
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Descriptive text */}
          <div className="space-y-3 text-[14px] leading-relaxed text-gray-600">
            <p>
              A potential match for the client has been found on sanctions lists and/or adverse
              media. Our system has highlighted the client as <RiskLevelBadge level={riskLevel} />.
              We recommend you generate a transparent report on matched data listed as low, medium
              and high as a PDF.
            </p>
            <p>
              You will also have the option to receive a pre-populated form based on your AML
              regulator's guidance to speed up the due diligence process.
            </p>
          </div>

          {/* Report offer */}
          <div className="rounded-lg border border-[#E5E7EB] p-4">
            <div className="flex items-start gap-3">
              <FileText className="h-6 w-6 flex-shrink-0 text-[#131313]" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-[16px] font-medium text-[#051923]">Match Risk Report</span>
                  <span className="text-[18px] font-semibold text-[#131313]">{priceLabel}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Balance check */}
          {balanceCheck &&
            !isCheckingBalance &&
            (hasSufficientBalance ? (
              <BalanceDisplay currentBalancePence={currentBalance} />
            ) : (
              <InsufficientBalance
                currentBalancePence={currentBalance}
                requiredAmountPence={balanceCheck.requiredAmountPence}
                shortfall={balanceCheck.shortfall}
                onAddFunds={onAddFunds}
              />
            ))}

          {/* EDD info callout */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex gap-3">
              <Info className="h-5 w-5 flex-shrink-0 text-blue-600" />
              <p className="text-[13px] text-gray-700">
                Want Enhanced Due Diligence? Purchase the Match Report first — you can then add EDD
                for {eddPrice ? eddPrice.priceFormatted : PRICE_LOADING_PLACEHOLDER}.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <Button
            onClick={onPurchase}
            disabled={!balanceCheck || !hasSufficientBalance || isPurchasing || isCheckingBalance}
            className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-none tracking-normal text-white hover:bg-[#131313]/90 disabled:opacity-50"
          >
            {isPurchasing ? 'Processing...' : `Purchase Match Report (${priceLabel})`}
          </Button>
          <button
            type="button"
            onClick={onSkip}
            disabled={isPurchasing}
            className="text-[14px] font-medium text-gray-500 underline hover:text-gray-700"
          >
            Skip Report — proceed without detailed findings
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
